// DVH (Dose Volume Histogram) data model and related types

export type CurveData = {
    data: { x: number; y: number }[];
    color: string;
    name: string;
    visible: boolean;
    doseMaxGy: number;
    doseMinGy: number;
    doseMeanGy: number;
    roiNumber: number;
    structureVolumeCm3?: number;
};

export class Dvh {
    public readonly dvhData: Float64Array; // DVH data, amount of cumulated structure volume at each bin
    public readonly binWidth: number; // Width of each bin in Gy
    public readonly doseMaxGy: number; // Maximum dose in Gy
    public readonly doseMinGy: number; // Minimum dose in Gy
    public readonly doseMeanGy: number; // Mean dose in Gy
    public readonly structureVolumeCm3?: number;

    constructor(
        dvhData: Float64Array,
        binWidth: number,
        doseMaxGy: number,
        doseMinGy: number,
        doseMeanGy: number,
        structureVolumeCm3?: number
    ) {
        this.dvhData = dvhData;
        this.binWidth = binWidth;
        this.doseMaxGy = doseMaxGy;
        this.doseMinGy = doseMinGy;
        this.doseMeanGy = doseMeanGy;
        this.structureVolumeCm3 = structureVolumeCm3;
    }
}

// D_x%: the highest dose (Gy) at least x % of the volume receives, linear between bins, never above Dmax.
export function doseAtVolumePercentGy(
    dvh: Dvh,
    volumePercent: number
): number | undefined {
    const data = dvh.dvhData;
    if (
        !Number.isFinite(volumePercent) ||
        volumePercent <= 0 ||
        volumePercent > 100 ||
        data.length === 0 ||
        data[0] < volumePercent
    ) {
        return undefined;
    }
    let i = 0;
    while (i + 1 < data.length && data[i + 1] >= volumePercent) {
        i++;
    }
    const upper = data[i];
    const lower = i + 1 < data.length ? data[i + 1] : 0;
    const fraction =
        upper > lower ? (upper - volumePercent) / (upper - lower) : 0;
    return Math.min((i + fraction) * dvh.binWidth, dvh.doseMaxGy);
}
